import React, { useEffect, useMemo, useRef, useState } from "react"
import styled, { keyframes } from "styled-components"

// Clara's answer, arriving a word at a time.
//
// Each word resolves out of a slight blur rather than simply appearing. The effect is
// small, and the reason is not decorative: an answer that lands as a finished block
// gives a reader no sense of progress, and the UTSW audit showed that a two to four
// second wait followed by a wall of text is a large part of why Clara felt slower than
// Copilot at comparable latency.
//
// The reveal is character-paced but word-quantised, so a long answer does not take
// proportionally longer to read out. Words are never split mid-render, because a word
// half-drawn reads as a glitch rather than as motion.

const streamIn = keyframes`
	from { opacity: 0; filter: blur(4px); }
	to   { opacity: 1; filter: blur(0); }
`

const blink = keyframes`
	0%, 45%  { opacity: 1; }
	55%, 100% { opacity: 0; }
`

const fadeIn = keyframes`
	from { opacity: 0; }
	to   { opacity: 1; }
`

// Roughly 22 words a second: fast enough that a long answer never feels withheld,
// slow enough to read as arriving rather than blinking into place.
const WORD_MS = 45
const MAX_TOTAL_MS = 2200

/* Clara's answers have structure, and it was being thrown away.
 *
 * The introduction and the "what can you see" answer are lists: eight signals, five
 * documents, a set of example questions. Rendered as one pre-wrapped string they arrive as
 * a wall with literal bullet characters in it, which is exactly the shape the UTSW audit
 * flagged as the reason Clara felt heavier to read than she is.
 *
 * So the text is parsed into blocks before rendering. Deliberately three rules and no
 * more, because this is not a markdown renderer and Clara's answers are written by us:
 *
 *   "• something"      a bullet, consecutive ones become one list
 *   "Something:"       a short line ending in a colon is a heading
 *   anything else      a paragraph
 *
 * A plain one sentence answer, which is most of them, parses to a single paragraph and
 * renders exactly as it did before.
 */
function parseBlocks(text) {
	const blocks = []
	let paragraph = []

	const flushParagraph = () => {
		if (paragraph.length) {
			blocks.push({ kind: "p", text: paragraph.join(" ") })
			paragraph = []
		}
	}

	for (const rawLine of (text || "").split("\n")) {
		const line = rawLine.trim()
		if (!line) {
			flushParagraph()
			continue
		}
		if (line.startsWith("•")) {
			flushParagraph()
			const item = line.replace(/^•\s*/, "")
			const last = blocks[blocks.length - 1]
			if (last && last.kind === "ul") {
				last.items.push(item)
			} else {
				blocks.push({ kind: "ul", items: [item] })
			}
			continue
		}
		// A heading is short, ends in a colon, and is not a sentence that happens to.
		if (line.endsWith(":") && line.length <= 60 && !/[.!?]/.test(line)) {
			flushParagraph()
			blocks.push({ kind: "h", text: line.replace(/:$/, "") })
			continue
		}
		paragraph.push(line)
	}
	flushParagraph()
	return blocks
}

// Reveal the words of one chunk within the answer's remaining budget, so a list never
// arrives faster than the prose above it.
function revealWords(chunk, budget) {
	if (budget <= 0) {
		return { shown: [], used: 0 }
	}
	const parts = chunk.split(/(\s+)/).filter(Boolean)
	const shown = []
	let used = 0
	for (const part of parts) {
		if (/^\s+$/.test(part)) {
			shown.push(part)
			continue
		}
		if (used >= budget) {
			break
		}
		shown.push(part)
		used += 1
	}
	return { shown, used }
}

/**
 * @param text      the full answer
 * @param animate   false replays nothing (reopened history, or reduced motion)
 * @param scrollRef the scroll container to keep pinned to the bottom
 * @param onDone    called once every word is out, used to reveal the sources
 */
export function StreamingAnswer({ text, animate = true, scrollRef, onDone }) {
	const words = React.useMemo(() => (text || "").split(/(\s+)/), [text])
	const [count, setCount] = useState(animate ? 0 : words.length)
	const doneRef = useRef(false)

	useEffect(() => {
		doneRef.current = false
		if (!animate) {
			setCount(words.length)
			onDone?.()
			doneRef.current = true
			return undefined
		}

		setCount(0)
		// Cap the total so a long policy answer does not crawl; short answers keep the
		// natural per-word pace.
		const perWord = Math.max(12, Math.min(WORD_MS, MAX_TOTAL_MS / Math.max(1, words.length)))
		let index = 0
		const id = setInterval(() => {
			index += 1
			setCount(index)
			const element = scrollRef?.current
			if (element) {
				element.scrollTop = element.scrollHeight
			}
			if (index >= words.length) {
				clearInterval(id)
				if (!doneRef.current) {
					doneRef.current = true
					onDone?.()
				}
			}
		}, perWord)
		return () => clearInterval(id)
		// onDone is intentionally not a dependency: callers pass an inline closure, and
		// re-running this on every parent render would restart the animation forever.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [text, animate, words.length, scrollRef])

	const streaming = count < words.length

	// Blocks keep their shape; the reveal is still counted across the whole answer, so a
	// list does not arrive faster than the prose above it.
	const blocks = useMemo(() => parseBlocks(text), [text])
	let budget = count
	const rendered = []

	const take = chunk => {
		const { shown, used } = revealWords(chunk, budget)
		budget -= used
		return shown
	}

	const spans = (parts, keyPrefix) =>
		parts.map((part, index) =>
			/^\s+$/.test(part) ? (
				part
			) : (
				<Word key={`${keyPrefix}-${index}`} $animate={animate}>
					{part}
				</Word>
			)
		)

	blocks.forEach((block, blockIndex) => {
		if (budget <= 0 && count < words.length) {
			return
		}
		if (block.kind === "h") {
			const parts = take(block.text)
			if (parts.length) {
				rendered.push(<Heading key={`h-${blockIndex}`}>{spans(parts, `h${blockIndex}`)}</Heading>)
			}
			return
		}
		if (block.kind === "ul") {
			const items = []
			block.items.forEach((item, itemIndex) => {
				const parts = take(item)
				if (parts.length) {
					items.push(<Item key={`li-${blockIndex}-${itemIndex}`}>{spans(parts, `li${blockIndex}x${itemIndex}`)}</Item>)
				}
			})
			if (items.length) {
				rendered.push(<List key={`ul-${blockIndex}`}>{items}</List>)
			}
			return
		}
		const parts = take(block.text)
		if (parts.length) {
			rendered.push(<Paragraph key={`p-${blockIndex}`}>{spans(parts, `p${blockIndex}`)}</Paragraph>)
		}
	})

	return (
		<Prose>
			{rendered}
			{streaming && <Caret aria-hidden />}
		</Prose>
	)
}

const Paragraph = styled.p`
	margin: 0;

	& + & {
		margin-top: 0.7em;
	}
`

/* A section label, not a heading in the document sense: it names the group under it, so
   it sits close to that group and leaves air above. */
const Heading = styled.div`
	margin: 1.05em 0 0.3em;
	font-weight: 650;
	font-size: 0.94em;
	letter-spacing: -0.005em;

	&:first-child {
		margin-top: 0;
	}
`

const List = styled.ul`
	margin: 0.15em 0 0;
	padding-left: 1.05em;
	list-style: none;
`

const Item = styled.li`
	position: relative;
	margin: 0.22em 0;

	&::before {
		content: "";
		position: absolute;
		left: -0.85em;
		top: 0.62em;
		width: 4px;
		height: 4px;
		border-radius: 50%;
		background: var(--color-accent-interactive, #207dea);
		opacity: 0.65;
	}
`

const Prose = styled.div`
	font-size: 15.5px;
	line-height: 1.62;
`

const Word = styled.span`
	display: inline;
	animation: ${({ $animate }) => ($animate ? streamIn : fadeIn)} 380ms cubic-bezier(0.22, 0.61, 0.25, 1) both;
	will-change: filter, opacity;

	/* A blur that never resolves would leave the answer unreadable, so reduced motion
	   drops to a plain fade rather than removing the reveal entirely. */
	@media (prefers-reduced-motion: reduce) {
		animation: ${fadeIn} 200ms ease-out both;
		will-change: auto;
	}
`

const Caret = styled.span`
	display: inline-block;
	width: 2px;
	height: 1em;
	margin-left: 2px;
	vertical-align: text-bottom;
	border-radius: 1px;
	background: var(--text-color);
	animation: ${blink} 1s steps(1, end) infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
		opacity: 0.6;
	}
`

export default StreamingAnswer
